import React from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
} from "@material-ui/core";
import { withStyles, withTheme } from "@material-ui/core/styles";
import { compose } from "recompose";
import MapView, {
  Marker,
  Popup,
  MapControls,
  MapConstant,
  SingleSearch,
} from "../../../../lib/Map";
import { CustomerLocationMarker } from "../../../mapComponents/Markers";
import { ZonePopUpInfo } from "../../../mapComponents/Popups";
import { postData } from "../../../../api/componentAPIs";
import { withSnackbar } from "notistack";
import Tooltip from "../../../common/StyledToolTip";
import MapsHelper from "../../../../lib/Map/helper";
import { GEOCODE_OPTIONS } from "./utils";
import { withAppConfig } from "../../../../appConfigContext";
import GeocoderSearch from "../GeocoderSearchBox";

const style = (theme) => ({
  dialog: {
    backgroundColor: theme.ModalBGColor,
    width: "85%",
  },
  dialogTitle: {
    fontSize: 14,
    fontWeight: "600",
    color: theme.HeaderSelectedText,
    padding: "8px 0",
  },
  dialogContent: {
    height: 600,
    width: "100%",
    boxSizing: "border-box",
    padding: "0 24px",
  },
  dialogAction: {
    padding: "16px 24px",
    margin: 0,
  },
  button: {
    textTransform: "none",
    "&$disabled": {
      color: theme.headerIconColor,
      borderColor: theme.headerIconColor,
    },
  },
  message: {
    fontSize: "13px",
    color: theme.gridTextColor,
  },
  geoCodingProgress: {
    top: "48%",
    width: "90%",
    textAlign: "center",
    position: "absolute",
    fontSize: theme.textFontSize,
    color: theme.TextColor,
    boxSizing: "border-box",
    marginLeft: "5%",
    padding: 5,
    backgroundColor: theme.loadingBGColor,
  },
  failMessage: {
    color: theme.ErrorMessageColor,
  },
  locationUpdated: {
    color: theme.HeaderSelectedText,
    textAlign: "center",
    minWidth: 110,
  },
  locationNotUpdated: {
    color: theme.ErrorMessageColor,
    textAlign: "center",
    minWidth: 110,
  },
  disabled: {},
  addressContainer: {
    display: "flex",
    justifyContent: "space-between",
  },
  searchContainer: {
    width: "33%",
  },
});

const LOCATION_UPDATE_STATUS = {
  PENDING: 0,
  SUCCESS: 1,
  FAILURE: 2,
};

class GeocodingModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      zoneInfo: null,
      updatedLocation: null,
      showCustomerLabel: true,
      locationUpdateStatus: LOCATION_UPDATE_STATUS.PENDING,
      address: "",
    };
  }

  componentDidMount() {
    this.geocodeLocation(this.props.customer);
  }

  getLocationDetails = ([longitude, latitude], zip, address) => {
    if (longitude && latitude && address) {
      this.setState({
        address: address,
        updatedLocation: {
          lng: longitude,
          lat: latitude,
        },
        showCustomerLabel: true,
      });
    } else {
      this.setState({
        address: "",
      });
    }
  };

  onMapClicked = () => {
    this.setState({
      zoneInfo: null,
      showCustomerLabel: true,
    });
  };

  onZoneClicked = (zoneInfo) => {
    this.setState({
      zoneInfo,
      showCustomerLabel: true,
    });
  };

  onCustomerLocationUpdated = ({ position }) => {
    this.setState({
      updatedLocation: {
        lng: position.lng,
        lat: position.lat,
      },
      address: "",
      showCustomerLabel: true,
    });
  };

  sanitizeAddress = (address) => {
    return address.replace(/[^a-zA-Z0-9 ]/g, "").trim(); // removes special characters at beginning of the string
  };

  geocodeLocation = (customer) => {
    const sanitizedAddress = this.sanitizeAddress(customer.address);
    this.fullAddress = [
      sanitizedAddress,
      customer.city,
      customer.state,
      customer.zip,
    ]
      .filter(Boolean)
      .join(",");
    if (customer.isGeoCoded) {
      this.setState({
        updatedLocation: {
          lng: customer.longitude,
          lat: customer.latitude,
        },
        locationUpdateStatus: LOCATION_UPDATE_STATUS.SUCCESS,
      });
    } else {
      SingleSearch(this.fullAddress).then((data) => {
        if (data && data.Locations.length > 0) {
          this.setState({
            updatedLocation: {
              lng: data.Locations[0].Coords.Lon,
              lat: data.Locations[0].Coords.Lat,
            },
            locationUpdateStatus: LOCATION_UPDATE_STATUS.SUCCESS,
          });
        } else {
          this.props.enqueueSnackbar(`Failed to geocode, please search the address`, {
            variant: "error",
          });
          this.onGeocodingFailure(customer);
        }
      });
    }
  };

  onGeocodingFailure = (customer) => {
    this.setState({
      updatedLocation: {
        lat: customer.latitude,
        lng: customer.longitude,
      },
      locationUpdateStatus: LOCATION_UPDATE_STATUS.FAILURE,
    });
  };

  onCustomerMarkerDragged = () => {
    if (this.state.showCustomerLabel) {
      this.setState({
        showCustomerLabel: false,
        locationUpdateStatus: LOCATION_UPDATE_STATUS.SUCCESS,
      });
    }
  };

  saveCustomerLocation = () => {
    if (this.state.updateInProgress) {
      return;
    }
    const { latitude, longitude } = this.props.customer;
    const { lat, lng } = this.state.updatedLocation;

    if (MapsHelper.distance([latitude, longitude], [lat, lng]) < 10) {
      this.props.enqueueSnackbar(`Customer location updated`, {
        variant: "success",
      });
      this.props.close();
      return;
    }

    this.setState({
      updateInProgress: true,
    });

    const geocodingOption = GEOCODE_OPTIONS[this.props.customer.type];
    if (!geocodingOption) {
      this.onSaveCustomerLocationFailure();
      return;
    }

    const apiName = geocodingOption.apiName;
    const requestParams = geocodingOption.getRequestParams?.(
      this.props.customer,
      this.state.updatedLocation,
      this.props.userData
    );

    postData(apiName, requestParams).then(
      (response) => {
        if (response && Math.trunc(response.status / 100) === 2) {
          this.props.enqueueSnackbar(
            `${
              this.props.customer.pickupId ? "Shipper" : "Customer"
            } location updated`,
            {
              variant: "success",
            }
          );
          this.props.close(true);
        } else {
          this.onSaveCustomerLocationFailure();
        }
      },
      (error) => {
        this.onSaveCustomerLocationFailure();
      }
    );
  };

  onSaveCustomerLocationFailure = () => {
    const errorMsg = `Failed to update  ${
      this.props.customer.pickupId ? "shipper" : "customer"
    } location`;

    this.props.enqueueSnackbar(errorMsg, { variant: "error" });
    this.setState({
      updateInProgress: false,
    });
  };

  getCustomerTooltip = () => {
    let message = "";
    const { classes } = this.props;

    switch (this.state.locationUpdateStatus) {
      case LOCATION_UPDATE_STATUS.SUCCESS:
        message = "Updated location";
        break;
      case LOCATION_UPDATE_STATUS.FAILURE:
        message = "Unable to update";
        break;
      default:
        break;
    }

    return (
      <React.Fragment>
        <div className={classes.locationUpdated}>{message}</div>
        <div>Drag to re-position</div>
      </React.Fragment>
    );
  };

  getLocationUpdateStatus = () => {
    switch (this.state.locationUpdateStatus) {
      case LOCATION_UPDATE_STATUS.PENDING:
        return (
          <span>{` ${this.props.customer.name} update is in progress`}</span>
        );
      case LOCATION_UPDATE_STATUS.SUCCESS:
        return <span>{` ${this.props.customer.name} was updated`}</span>;
      case LOCATION_UPDATE_STATUS.FAILURE:
      default:
        return (
          <span className={this.props.classes.failMessage}>
            {` Could not update ${this.props.customer.name} location`}
          </span>
        );
    }
  };

  render() {
    const { theme, classes, customer } = this.props;
    const { updatedLocation } = this.state;
    return (
      <div>
        <Dialog
          disableBackdropClick
          open={true}
          classes={{ paper: classes.dialog }}
          aria-labelledby="popup-title"
          maxWidth="lg"
        >
          <DialogTitle id="popup-title" disableTypography>
            <div className={classes.dialogTitle}>
              <span>
                Correct Location:
                {this.getLocationUpdateStatus()}
              </span>
            </div>
            <div className={classes.addressContainer}>
              <div className={classes.message}>
                <span>{`Freight Bill Address: ${this.fullAddress}`}</span>
                <br />
                {customer.isMiscellaneousCustomer ? (
                  <span>Customer#: Miscellaneous</span>
                ) : (
                  <span>{`Customer#: ${customer.number}`}</span>
                )}

                {this.state.locationUpdateStatus ===
                  LOCATION_UPDATE_STATUS.SUCCESS && (
                  <div>
                    <span>Would you like to save the new location?</span>
                    <br />
                    <span>
                      If marker is not correctly placed on the map, please drag
                      to re-position
                    </span>
                  </div>
                )}
                {this.state.locationUpdateStatus ===
                  LOCATION_UPDATE_STATUS.FAILURE && (
                  <div>Adjust the marker on the map (or) search for a nearby address to update the location</div>
                )}
              </div>
              <div className={classes.searchContainer}>
                <GeocoderSearch
                  getLocationDetails={this.getLocationDetails}
                  inputValue={this.state.address}
                  geocoderTheme={theme.themeName}
                />
              </div>
            </div>
          </DialogTitle>
          <DialogContent classes={{ root: classes.dialogContent }}>
            {updatedLocation ? (
              <MapView
                zoom={18}
                onClick={this.onMapClicked}
                mapId="geoCodingMap"
                satteliteView
                mapTheme={theme.themeName}
                center={updatedLocation}
                mapConfig={this.props.configJSON?.maps}
              >
                <MapControls
                  controlOptions={{
                    zoomControl: {
                      position: MapConstant.CONTROL_POSITION.BOTTOM_RIGHT,
                    },
                    satelliteViewControl: {
                      position: MapConstant.CONTROL_POSITION.BOTTOM_RIGHT,
                    },
                    reCenterMapControl: {
                      position: MapConstant.CONTROL_POSITION.BOTTOM_RIGHT,
                    },
                    showZones: {
                      position: MapConstant.CONTROL_POSITION.BOTTOM_RIGHT,
                      terminalID: this.props.userData.terminal?.code,
                      geoJson: this.props.userData.geoJson,
                      onZoneClicked: this.onZoneClicked,
                    },
                  }}
                />
                <Marker
                  data={customer}
                  position={updatedLocation}
                  key={customer.id || customer.rmaxShipperCode}
                  draggable={true}
                  onDrag={this.onCustomerMarkerDragged}
                  onDragEnd={this.onCustomerLocationUpdated}
                  forceUpdate={true}
                  isActiveShipment={true}
                >
                  <Tooltip
                    title={
                      this.state.showCustomerLabel
                        ? this.getCustomerTooltip()
                        : ""
                    }
                    open
                    placement="top"
                    PopperProps={{ disablePortal: true }}
                  >
                    <CustomerLocationMarker />
                  </Tooltip>
                </Marker>

                {this.state.zoneInfo && (
                  <Popup isCloseButton position={this.state.zoneInfo.position}>
                    <ZonePopUpInfo data={this.state.zoneInfo.data} />
                  </Popup>
                )}
              </MapView>
            ) : (
              <div className={classes.geoCodingProgress}>
                Location update is in progress
              </div>
            )}
          </DialogContent>
          <DialogActions classes={{ root: classes.dialogAction }}>
            <Button
              variant="outlined"
              color="primary"
              size="medium"
              classes={{ root: classes.button, disabled: classes.disabled }}
              onClick={() => this.props.close()}
              disabled={this.state.updateInProgress}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="medium"
              classes={{ root: classes.button, disabled: classes.disabled }}
              onClick={this.saveCustomerLocation}
              disabled={!this.state.updatedLocation}
            >
              {this.state.updateInProgress ? "Saving....." : "Save location"}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }
}

export default compose(
  withStyles(style),
  withTheme(),
  withSnackbar,
  withAppConfig
)(GeocodingModal);
